import React, {
    useState,
    Suspense,
    useMemo,
    useCallback,
    Component,
    ErrorInfo,
    ReactNode,
    useEffect,
} from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { Spin } from 'antd';
import {
    ReportingWrapper,
    TabsOuterContainer,
    TabsContainer,
    ContentContainer,
    LoadingFallbackContainer,
    ErrorContainer,
    ErrorTitle,
    ErrorMessage,
    RetryButton,
} from './ReportingPage.styles';
import { useAppDispatch } from '../../../../hooks/redux';
import {
    getProjectProductsAction,
    getAllProjectProductUploadsAction,
    getAllActiveDocumentTypesAction,
} from '../../../../redux/actions/productActions';
import { getExternalProductsAction } from '../../../../redux/actions/transparencyCatalogActions';
import { ActionResponse } from '../../../../models/actionResponse/types';
import { InternalProduct, Product } from '../../../../models/product/types';
import { GetProductsFromTransparencyCatalogAPIResponse } from '../../../../redux/actions/transparencyCatalogActions/types';
import { GetDocumentTypesResponse } from '../../../../redux/actions/productActions/types';
import { mapLowEmittingData } from '../../../../helpers/lowEmittingHelper';
import { mergeInternalProductsWithExternal } from '../../../../helpers/mergeProjectProducts';
import { flattenTcMfSectionProducts } from '../../../../helpers/tcExternalProducts';
import {
    DocumentImpactAreaMapping,
    UploadedDocument,
    prepareCMFImpactAreaMapping
} from './tabs/common/cmfSharedUtils';

// Lazy load tab components for code splitting
const ProjectCMFAlignment = React.lazy(() => import('./tabs/ProjectCMF/ProjectCMFAlignment'));
const LEEDv5BPSP = React.lazy(() => import('./tabs/LEEDv5BPSP/LEEDv5BPSP'));
const AIAMaterials = React.lazy(() => import('./tabs/AIAMaterial/AIAMaterialPledge'));
const LEEDv5MEP = React.lazy(() => import('./tabs/LeedMEP/LEEDv5MEP'));
const LEEDv5LEM = React.lazy(() => import('./tabs/LeedLEM/LEEDv5LEM'));
const LEEDv5Other = React.lazy(() => import('./tabs/LEEDv5Other/LEEDv5Other'));

// Loading fallback component - memoized to prevent re-renders
const TabLoadingFallback = React.memo(() => (
    <LoadingFallbackContainer>Loading...</LoadingFallbackContainer>
));

TabLoadingFallback.displayName = 'TabLoadingFallback';

// Error boundary for lazy-loaded components
interface ErrorBoundaryState {
    hasError: boolean;
    error?: Error;
    isChunkError?: boolean;
    isReloaded?: boolean;
}

const CHUNK_ERROR_RELOAD_WINDOW = 60000; // 1 minute
const CHUNK_ERROR_RELOAD_KEY = 'chunkErrorReload';

class TabErrorBoundary extends Component<
    { children: ReactNode; fallback?: ReactNode, activeTab?: string },
    ErrorBoundaryState
> {
    constructor(props: any) {
        super(props);
        this.state = {
            hasError: false,
            error: undefined,
            isChunkError: false,
            isReloaded: false,
        };
    }

    static getDerivedStateFromError(error: Error): ErrorBoundaryState {
        const isChunkError = TabErrorBoundary.isChunkError(error);

        return {
            hasError: true,
            error,
            isChunkError,
            isReloaded: false,
        };
    }

    componentDidCatch(error: Error): void {
        if (
            TabErrorBoundary.isChunkError(error) &&
            !this.hasRecentlyReloaded()
        ) {
            localStorage.setItem(CHUNK_ERROR_RELOAD_KEY, Date.now().toString());
            window.location.reload();
        }
        else if (this.hasRecentlyReloaded()) {
            this.setState({ isReloaded: true });
        }
    }

    componentDidMount(): void {
        const isReloaded = this.hasRecentlyReloaded();
        this.setState({ isReloaded });

        setTimeout(() => {
            localStorage.removeItem(CHUNK_ERROR_RELOAD_KEY);
        }, CHUNK_ERROR_RELOAD_WINDOW);
    }

    componentDidUpdate(prevProps: { activeTab?: string }): void {
        if (prevProps.activeTab !== this.props.activeTab) {
            this.setState({
                hasError: false, error: undefined, isChunkError: false, isReloaded: false
            });
        }
    }

    static isChunkError(error: Error): boolean {
        return (
            error?.name === 'ChunkLoadError' ||
            error?.message?.includes('Failed to fetch dynamically imported module')
        );
    }

    private hasRecentlyReloaded(): boolean {
        const lastReload = Number(localStorage.getItem(CHUNK_ERROR_RELOAD_KEY));
        return Date.now() - lastReload < CHUNK_ERROR_RELOAD_WINDOW;
    }

    render(): ReactNode {
        if (this.state.hasError) {
            if (this.state.isChunkError) {
                return this.state.isReloaded
                    ? (
                        <LoadingFallbackContainer>
                            Something Went Wrong!
                        </LoadingFallbackContainer>
                    )
                    : this.props.fallback || null;
            }

            return (
                this.props.fallback || (
                    <ErrorContainer>
                        <ErrorTitle>Error loading tab</ErrorTitle>
                        <ErrorMessage>
                            {this.state.error?.message || 'An error occurred.'}
                        </ErrorMessage>
                        <RetryButton
                            onClick={() =>
                                this.setState({
                                    hasError: false,
                                    error: undefined,
                                    isChunkError: false,
                                })
                            }
                        >
                            Try Again
                        </RetryButton>
                    </ErrorContainer>
                )
            );
        }

        return this.props.children;
    }
}

// Tab configuration - components are loaded lazily
const tabs = [
    { key: 'cmf', label: 'Project CMF alignment', component: ProjectCMFAlignment },
    { key: 'aia', label: 'AIA Materials Pledge', component: AIAMaterials },
    { key: 'leedbpsp', label: 'LEED v5 MR: BPSP', component: LEEDv5BPSP },
    { key: 'leedmep', label: 'LEED v5 MR: MEP', component: LEEDv5MEP },
    { key: 'leedother', label: 'LEED v5 MR: Other', component: LEEDv5Other },
    { key: 'leedlem', label: 'LEED v5 MR: LEM', component: LEEDv5LEM },
] as const;

interface Params {
    id: string;
    tab?: string;
}

interface ProjectInfo {
    readonly id?: number;
    readonly name?: string;
}

function ReportingPage({ projectInfo }: { readonly projectInfo?: ProjectInfo | null }) {
    const dispatch = useAppDispatch();
    const params = useParams<Params>();
    const [activeTab, setActiveTab] = useState('cmf');
    const [products, setProducts] = useState<Product[]>([]);
    const [loading, setLoading] = useState(true);
    const [documentTypes, setDocumentTypes] = useState<GetDocumentTypesResponse[]>([]);
    const [cmfImpactAreaMappings, setCmfImpactAreaMappings] = useState<DocumentImpactAreaMapping[]>(
        [],
    );
    const [documentTypesLoaded, setDocumentTypesLoaded] = useState(false);
    const [uploadsLoaded, setUploadsLoaded] = useState(false);

    const history = useHistory();
    const location = useLocation();

    const reportingDataReady = !loading && documentTypesLoaded && uploadsLoaded;

    // Sync tab with `?tab=` query param and update URL when user clicks a tab
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const tabFromUrl = params.get('tab');
        if (tabFromUrl) {
            setActiveTab(tabFromUrl);
        }
    }, [location.search]);

    const handleTabsChange = (key: string) => {
        setActiveTab(key);
        const params = new URLSearchParams(location.search);
        params.set('tab', key);
        history.push(`${globalThis.location.pathname}?${params.toString()}`);
    };

    // Fetch document types once at parent level
    useEffect(() => {
        const fetchDocumentTypes = async () => {
            try {
                const { payload } = (await dispatch(
                    getAllActiveDocumentTypesAction(),
                )) as ActionResponse<GetDocumentTypesResponse[]>;

                if (payload?.data) {
                    setDocumentTypes(payload.data);
                }
            } catch (error) {
                console.error('Error fetching document types:', error);
                setDocumentTypes([]);
            } finally {
                setDocumentTypesLoaded(true);
            }
        };

        fetchDocumentTypes();
    }, [dispatch]);

    /**
     * Fetch uploaded documents and prepare CMF impact area mappings.
     *
     * Note: uploads can happen outside ReportingPage (e.g. on the product page),
     * so we refresh this dataset whenever the user switches report tabs (especially
     * when they open LEED BPSP right after uploading a document).
     */
    const fetchUploadedDocuments = useCallback(async () => {
        if (!params.id) {
            return;
        }
        if (!documentTypesLoaded) {
            return;
        }
        if (documentTypes.length === 0) {
            setCmfImpactAreaMappings([]);
            setUploadsLoaded(true);
            return;
        }

        setUploadsLoaded(false);
        try {
            const action = await dispatch(getAllProjectProductUploadsAction());
            if ((action as any)?.payload?.data) {
                const uploadedDocuments = (action as any)?.payload?.data as UploadedDocument[];

                if (uploadedDocuments.length === 0) {
                    setCmfImpactAreaMappings([]);
                    return;
                }

                const mappings = prepareCMFImpactAreaMapping(uploadedDocuments, documentTypes);
                setCmfImpactAreaMappings(mappings);
            } else {
                setCmfImpactAreaMappings([]);
            }
        } catch (error) {
            console.error('Error fetching project product uploads:', error);
            setCmfImpactAreaMappings([]);
        } finally {
            setUploadsLoaded(true);
        }
    }, [params.id, dispatch, documentTypes, documentTypesLoaded]);

    // Initial fetch (once document types are available) + refresh on tab changes.
    useEffect(() => {
        void fetchUploadedDocuments();
    }, [fetchUploadedDocuments, activeTab]);

    // Fetch products once at parent level
    useEffect(() => {
        const fetchProducts = async () => {
            if (!params.id) {
                return;
            }

            setLoading(true);
            try {
                // 1) Fetch internal products
                const { payload: internalProductsPayload } = (await dispatch(
                    getProjectProductsAction(+params.id),
                )) as ActionResponse<Array<InternalProduct>>;

                const hasInternal =
                    internalProductsPayload?.data && internalProductsPayload.data.length > 0;

                if (!hasInternal) {
                    setProducts([]);
                    setLoading(false);
                    return;
                }

                const productIds = internalProductsPayload.data.map((p) => p.productId);

                // 2) Fetch external products
                const { payload: externalProductsPayload } = (await dispatch(
                    getExternalProductsAction({ productIds }),
                )) as ActionResponse<GetProductsFromTransparencyCatalogAPIResponse>;

                if (!externalProductsPayload?.data) {
                    setProducts([]);
                    setLoading(false);
                    return;
                }

                // 3) Flatten mfSection buckets, then merge with internal rows (same as Products tab)
                const flatExternal = flattenTcMfSectionProducts(
                    externalProductsPayload.data.mfSection,
                );

                const mergedProducts = mergeInternalProductsWithExternal(
                    internalProductsPayload.data,
                    flatExternal,
                );

                await mapLowEmittingData(mergedProducts, dispatch);
                setProducts(mergedProducts);
            } catch (error) {
                console.error('Error fetching products:', error);
                setProducts([]);
            } finally {
                setLoading(false);
            }
        };

        fetchProducts();
    }, [params.id, dispatch]);

    // Memoize active tab component to prevent unnecessary re-renders
    const ActiveTabComponent = useMemo(() => {
        const tab = tabs.find((t) => t.key === activeTab);
        return tab?.component || null;
    }, [activeTab]);

    return (
        <ReportingWrapper>
            <TabsOuterContainer>
                <TabsContainer>
                    {tabs.map((tab) => (
                        <button
                            key={tab.key}
                            className={activeTab === tab.key ? 'active' : ''}
                            onClick={() => handleTabsChange(tab.key)}
                        >
                            {tab.label}
                        </button>
                    ))}
                </TabsContainer>
            </TabsOuterContainer>

            <ContentContainer>
                {loading ? (
                    <LoadingFallbackContainer>
                        <Spin size="large" tip="Loading products..." />
                    </LoadingFallbackContainer>
                ) : (
                    <TabErrorBoundary fallback={<TabLoadingFallback />} activeTab={activeTab}>
                        <Suspense fallback={<TabLoadingFallback />}>
                            {ActiveTabComponent && (
                                <ActiveTabComponent
                                    products={products}
                                    loading={loading}
                                    reportingDataReady={reportingDataReady}
                                    documentTypes={documentTypes}
                                    cmfImpactAreaMappings={cmfImpactAreaMappings}
                                    projectName={projectInfo?.name}
                                />
                            )}
                        </Suspense>
                    </TabErrorBoundary>
                )}
            </ContentContainer>
        </ReportingWrapper>
    );
}

// Memoize the component to prevent unnecessary re-renders
export default React.memo(ReportingPage);
